import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { useDataTableState } from "@trillion/ui";
import { adminApi } from "@/lib/admin-api";
import { getArticleColumns } from "@/components/articles/article-columns";
import type { Article } from "@/types";
import { ArticleStatus } from "@/types/enums";

const PAGE_SIZE = 20;

export function ArticlesPage() {
  const navigate = useNavigate();
  // Radix Select is string-valued, so the "all" sentinel stays a string and the conversion to the
  // numeric enum happens where the filter is built.
  const [statusFilter, setStatusFilter] = useState("all");

  const t = useDataTableState<Article>({
    fetcher: async (p) => {
      const result = await adminApi.articles.list({
        page: p.page,
        pageSize: p.pageSize,
        search: p.search || undefined,
        articleStatusId: p.filters.articleStatusId as ArticleStatus | undefined,
        sortBy: p.sortBy,
        sortDesc: p.sortDesc,
      });
      return { items: result.items, totalCount: result.total_count };
    },
    onDelete: async (article) => {
      await adminApi.articles.delete(article.article_id);
      toast.success("Article deleted");
    },
    filters: { articleStatusId: statusFilter !== "all" ? Number(statusFilter) : undefined },
    pageSize: PAGE_SIZE,
    onError: (err) =>
      toast.error("Failed to load articles", {
        description: err instanceof Error ? err.message : undefined,
      }),
  });

  const { setDeleteTarget } = t;
  const columns = useMemo(
    () =>
      getArticleColumns({
        onEdit: (article) => navigate(`/articles/${article.article_id}`),
        onDelete: (article) => setDeleteTarget(article),
      }),
    [navigate, setDeleteTarget]
  );

  const table = useReactTable({
    data: t.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Articles"
        description="Manage help articles"
        actions={
          <Button onClick={() => navigate("/articles/new")}>
            <Plus className="mr-2 size-4" />
            New Article
          </Button>
        }
      />

      <DataTableWrapper
        title="All Articles"
        description={`${t.totalCount} article${t.totalCount !== 1 ? "s" : ""} total`}
        isLoading={t.isLoading}
        toolbar={
          <div className="flex items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={t.search}
                onChange={(e) => t.setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value={String(ArticleStatus.Draft)}>Draft</SelectItem>
                <SelectItem value={String(ArticleStatus.Published)}>Published</SelectItem>
                <SelectItem value={String(ArticleStatus.Archived)}>Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        pagination={
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {t.page} of {t.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => t.setPage(Math.max(1, t.page - 1))}
                disabled={t.page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => t.setPage(t.page + 1)}
                disabled={t.page >= t.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        }
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No articles found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </DataTableWrapper>

      <ConfirmDialog
        open={t.deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) t.setDeleteTarget(null);
        }}
        title="Delete Article"
        description={`Are you sure you want to delete "${t.deleteTarget?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={t.confirmDelete}
        variant="destructive"
        isLoading={t.isDeleting}
      />
    </div>
  );
}
